import React from "react";
import { useCountdown } from "../hooks/useCountdown";
import { getEventStatus, STATUS_LABELS, resolveImageUrl } from "../lib/eventsUtils";
import { Clock, Calculator as CalcIcon, Info, ImageOff } from "lucide-react";

const StatusBadge = ({ status }) => {
  const map = {
    upcoming: "bg-[#1B2E52]/60 border-[#4A6FA5]/25 text-[#B9CDE8]",
    live: "bg-[#0F3A2A]/60 border-emerald-600/30 text-emerald-200",
    ended: "bg-slate-800/60 border-slate-600/30 text-slate-300",
  };
  const dot = {
    upcoming: "bg-[#8AAEDD]",
    live: "bg-emerald-400 animate-pulse",
    ended: "bg-slate-400",
  }[status];
  return (
    <div
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border ${map[status]} text-[9px] font-semibold backdrop-blur-md`}
    >
      <span className={`w-1 h-1 rounded-full ${dot}`} />
      <span>{STATUS_LABELS[status]}</span>
    </div>
  );
};

const CountdownUnit = ({ value, label }) => (
  <div className="flex flex-col items-center">
    <div className="w-full rounded-md bg-[#0A1128]/70 border border-[#B89968]/15 py-2 text-center">
      <div
        key={value}
        className="countdown-digit text-[#F5F1E8] font-black text-xl sm:text-[22px] tabular-nums leading-none"
      >
        {String(value).padStart(2, "0")}
      </div>
    </div>
    <div className="text-[8px] text-slate-400 mt-1 font-medium tracking-wide">
      {label}
    </div>
  </div>
);

const Countdown = ({ target, label }) => {
  const { days, hours, minutes, seconds } = useCountdown(target);
  return (
    <div className="rounded-xl bg-white/[0.015] border border-white/5 px-3 py-2.5">
      <div className="flex items-center justify-center gap-1.5 mb-1.5 text-slate-400 text-[10px]">
        <Clock className="w-3 h-3" />
        <span>{label}</span>
      </div>
      {/* RTL-friendly grid; days on right, seconds on left visually */}
      <div className="grid grid-cols-4 gap-2">
        <CountdownUnit value={days} label="يوم" />
        <CountdownUnit value={hours} label="ساعة" />
        <CountdownUnit value={minutes} label="دقيقة" />
        <CountdownUnit value={seconds} label="ثانية" />
      </div>
    </div>
  );
};

const EventCard = ({ event, onOpenDetails, onCalculate }) => {
  const status = getEventStatus(event);
  const imageUrl = resolveImageUrl(event.image);
  const target =
    status === "upcoming"
      ? event.startsAt
      : status === "live"
      ? event.endsAt
      : null;
  const countdownLabel =
    status === "upcoming"
      ? "تبدأ بعد"
      : status === "live"
      ? "تنتهي خلال"
      : null;

  return (
    <article className="event-card group rounded-2xl bg-gradient-to-b from-[#0D1A38]/80 to-[#0A1128]/95 border border-[#B89968]/12 shadow-[0_6px_20px_rgba(0,0,0,0.28)] overflow-hidden flex flex-col">
      {/* Larger Image (5-8% taller aspect) */}
      <div className="relative aspect-[16/10.2] overflow-hidden bg-[#060B1E]">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={event.name}
            className="event-img w-full h-full object-cover scale-[1.06]"
            loading="lazy"
            draggable={false}
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#12224F] via-[#0D1A3E] to-[#0A1128] flex flex-col items-center justify-center gap-2">
            <ImageOff className="w-8 h-8 text-[#C9A96A]/40" />
            <span className="text-[#C9A96A]/70 text-[11px] font-semibold text-center px-3">
              {event.name}
            </span>
          </div>
        )}
        {/* Blended bottom gradient */}
        <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-[#0A1128] via-[#0A1128]/85 to-transparent pointer-events-none" />
        {/* Status badge */}
        <div className="absolute top-2 start-2">
          <StatusBadge status={status} />
        </div>
        {event.featured && (
          <div className="absolute top-2 end-2 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-[#B89968]/12 border border-[#B89968]/22 text-[#D9BC7E] text-[9px] font-semibold backdrop-blur-md">
            ★ مميزة
          </div>
        )}
      </div>

      {/* Compact content */}
      <div className="p-4 flex-1 flex flex-col gap-3">
        {/* Title & type */}
        <div>
          <h3 className="text-white font-black text-[22px] sm:text-2xl leading-tight">
            {event.name}
          </h3>
          <p className="text-[#B89968] text-[11px] font-medium mt-1 tracking-wide">
            {event.type}
          </p>
        </div>

        {/* Countdown centered */}
        {target && countdownLabel && (
          <Countdown target={target} label={countdownLabel} />
        )}
        {event.dateUncertain && status !== "ended" && (
          <div
            data-testid={`event-date-uncertain-${event.id}`}
            className="rounded-xl bg-amber-500/10 border border-amber-500/25 text-amber-200 text-[11px] text-center py-2 font-semibold"
          >
            الموعد غير مؤكد
          </div>
        )}
        {status === "ended" && (
          <div className="rounded-xl bg-slate-800/40 border border-slate-700/50 text-slate-300 text-xs text-center py-2.5 font-semibold">
            انتهت العجلة
          </div>
        )}

        {/* Actions - equal size, primary emphasized */}
        <div className={`grid ${event.wheelId ? "grid-cols-2" : "grid-cols-1"} gap-2 mt-auto`}>
          {event.wheelId && (
            <button
              type="button"
              onClick={() => onCalculate?.(event)}
              className="event-btn-primary h-11 inline-flex items-center justify-center gap-1.5 rounded-xl bg-gradient-to-b from-[#D4B278] to-[#B08A4A] text-[#0A1128] font-bold text-sm shadow-[0_4px_14px_rgba(184,153,104,0.25)]"
            >
              <CalcIcon className="w-4 h-4" />
              احسب التكلفة
            </button>
          )}
          <button
            type="button"
            onClick={() => onOpenDetails?.(event)}
            className="event-btn-secondary h-11 inline-flex items-center justify-center gap-1.5 rounded-xl bg-white/[0.03] border border-white/10 text-slate-200 font-semibold text-sm"
          >
            <Info className="w-4 h-4" />
            عرض التفاصيل
          </button>
        </div>
      </div>
    </article>
  );
};

export default EventCard;
